import { useCallback, useState } from 'react'

import { useFormContext } from 'react-hook-form'

import { Post, PostStatus } from '@tribeplatform/gql-client/types'
import { useAuthMember } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { DateValue } from '@tribeplatform/react-ui-kit/DateTimePicker'
import { confirm } from '@tribeplatform/react-ui-kit/Dialog'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { T, useI18n } from '../../i18n/index.js'
import { PostFormFields } from '../types.js'
import { SubmitPostProps } from '../useSubmitPost.js'
import { ScheduledPostInfo } from './ScheduledPostInfo.js'
import { SchedulePostPopup } from './SchedulePostPopup.js'

interface Props {
  loading: boolean
  disabled?: boolean
  onSubmit: (props: SubmitPostProps) => Promise<unknown>
  onCancel?: () => void
  post: Post
}

export const PostFormActions = ({
  disabled,
  loading,
  onSubmit,
  onCancel,
  post,
}: Props) => {
  const formCtx = useFormContext()
  const { $t } = useI18n()
  const isDraft = post?.status === PostStatus.DRAFT
  const isPublished = post?.status === PostStatus.PUBLISHED
  const isNew = !post?.id
  const [schedulePopupOpen, setSchedulePopupOpen] = useState(false)
  const { isModerator } = useAuthMember()
  const currentScheduledFor = formCtx.watch('scheduledFor')

  const handleSubmit = useCallback(
    async (data: PostFormFields) => {
      const formData = {
        ...data,
        scheduledFor: data.scheduledFor?.toString(),
      }
      try {
        const draft = isDraft || !!data.scheduledFor
        await onSubmit({
          formData,
          methods: formCtx,
          type: draft ? 'draft' : 'publish',
        })
        if (data.scheduledFor) {
          toast({
            title: $t({
              id: 'Generics.Success',
              defaultMessage: 'Success',
            }),
            description: $t(
              {
                id: 'Post.Schedule.Success',
                defaultMessage: 'Post scheduled for {date}',
              },
              {
                date: new Date(data.scheduledFor).toLocaleString(),
              },
            ),
            status: 'success',
          })
          return
        }

        if (isDraft) {
          toast({
            title: $t({
              id: 'Generics.Success',
              defaultMessage: 'Success',
            }),
            description: $t({
              id: 'Post.SaveAsDraft.Success',
              defaultMessage:
                'Draft saved. See all drafts in the moderation panel.',
            }),
            status: 'success',
          })
        }
      } catch (error) {
        toast({
          title: $t({
            id: 'Generics.Error',
            defaultMessage: 'Error',
          }),
          description: $t({
            id: 'Generics.SomethingWentWrong',
            defaultMessage: 'Something went wrong',
          }),
          status: 'error',
        })
      }
    },
    [isDraft, onSubmit, formCtx, $t],
  )

  const setScheduledFor = useCallback(
    (value: DateValue) => {
      formCtx.setValue('scheduledFor', value)
      setSchedulePopupOpen(false)
      formCtx.handleSubmit(handleSubmit)()

      if (!formCtx.formState.isValid) {
        formCtx.setValue('scheduledFor', null)
      }
    },
    [formCtx, handleSubmit],
  )
  const saveAsDraft = async (data: PostFormFields) => {
    try {
      await onSubmit({
        formData: data,
        methods: formCtx,
        type: 'draft',
      })
      toast({
        title: $t({
          id: 'Generics.Success',
          defaultMessage: 'Success',
        }),
        description: $t({
          id: 'Post.SaveAsDraft.Success',
          defaultMessage:
            'Draft saved. See all drafts in the moderation panel.',
        }),
        status: 'success',
      })
    } catch (error) {
      toast({
        title: $t({
          id: 'Generics.Error',
          defaultMessage: 'Error',
        }),
        description: $t({
          id: 'Generics.SomethingWentWrong',
          defaultMessage: 'Something went wrong',
        }),
        status: 'error',
      })
    }
  }

  const publishDraftPost = async (data: PostFormFields) => {
    try {
      await onSubmit({
        formData: data,
        methods: formCtx,
        type: 'publish',
      })
    } catch (error) {
      toast({
        title: $t({
          id: 'Generics.Error',
          defaultMessage: 'Error',
        }),
        description: $t({
          id: 'Generics.SomethingWentWrong',
          defaultMessage: 'Something went wrong',
        }),
        status: 'error',
      })
    }
  }

  const unschedulePost = async (data: PostFormFields) => {
    if (isNew || !post?.scheduledFor) {
      setScheduledFor(null)
      return
    }

    const confirmed = await confirm({
      title: 'Unschedule post',
      description: 'The post will be unscheduled and moved to draft state.',
    })
    if (confirmed) {
      try {
        await onSubmit({
          formData: { ...data, scheduledFor: null },
          methods: formCtx,
          type: 'draft',
        })
        setScheduledFor(null)
        toast({
          title: $t({
            id: 'Generics.Success',
            defaultMessage: 'Success',
          }),
          description: $t({
            id: 'Post.Unschedule.Success',
            defaultMessage: 'Post is unscheduled and converted to draft.',
          }),
          status: 'success',
        })
      } catch (error) {
        toast({
          title: $t({
            id: 'Generics.Error',
            defaultMessage: 'Error',
          }),
          description: $t({
            id: 'Generics.SomethingWentWrong',
            defaultMessage: 'Something went wrong',
          }),
          status: 'error',
        })
      }
    }
  }

  if (isPublished) {
    return (
      <Button
        size="lg"
        loading={loading}
        disabled={disabled || loading}
        onClick={formCtx.handleSubmit(handleSubmit)}
      >
        <T id="Generics.Update" defaultMessage="Update" />
      </Button>
    )
  }

  if (!isModerator) {
    return (
      <div className="flex gap-2">
        {!!onCancel && (
          <Button
            size="lg"
            variant="secondaryNeutral"
            onClick={() => onCancel()}
            disabled={disabled}
          >
            <T id="Generics.Cancel" defaultMessage="Cancel" />
          </Button>
        )}
        <Button
          size="lg"
          loading={loading}
          disabled={disabled || loading}
          onClick={formCtx.handleSubmit(handleSubmit)}
        >
          {!isNew ? (
            <T id="Generics.Update" defaultMessage="Update" />
          ) : (
            <T id="Generics.Publish" defaultMessage="Publish" />
          )}
        </Button>
      </div>
    )
  }

  return (
    <div className="flex gap-3 w-full justify-end items-center flex-wrap">
      <ScheduledPostInfo postId={post?.id} scheduledFor={currentScheduledFor} />
      <div className="flex gap-2">
        {!!onCancel && (
          <Button
            size="lg"
            variant="secondaryNeutral"
            onClick={onCancel}
            disabled={disabled}
          >
            <T id="Generics.Cancel" defaultMessage="Cancel" />
          </Button>
        )}
        <div className="flex">
          <Button
            loading={loading}
            className="rounded-e-none !px-6"
            disabled={disabled || loading}
            onClick={formCtx.handleSubmit(handleSubmit)}
          >
            {isNew ? (
              <T id="Generics.Publish" defaultMessage="Publish" />
            ) : (
              <T id="Generics.Update" defaultMessage="Update" />
            )}
          </Button>
          <Dropdown placement="top-end">
            <Dropdown.ButtonMinimal disabled={disabled || loading} as="div">
              <Button
                className="rounded-s-none !px-2 border-0 border-s !border-[var(--bm-color-action-primary-hovered)]"
                disabled={disabled || loading}
              >
                <SvgIcon name="chevron-down" size="lg" />
              </Button>
            </Dropdown.ButtonMinimal>
            <Dropdown.Items>
              {isNew && (
                <Dropdown.Item
                  leadingIcon={<SvgIcon name="notebook-pen" />}
                  onClick={formCtx.handleSubmit(saveAsDraft)}
                >
                  <T id="Post.SaveAsDraft" defaultMessage="Save as draft" />
                </Dropdown.Item>
              )}
              {(isDraft || !!post?.scheduledFor) && (
                <Dropdown.Item
                  leadingIcon={<SvgIcon name="send" />}
                  onClick={formCtx.handleSubmit(publishDraftPost)}
                >
                  <T id="Post.PublishNow" defaultMessage="Publish now" />
                </Dropdown.Item>
              )}
              {!currentScheduledFor && (
                <Dropdown.Item
                  leadingIcon={<SvgIcon name="calendar" />}
                  onClick={() => setSchedulePopupOpen(true)}
                >
                  <T id="Generics.Schedule" defaultMessage="Schedule" />
                </Dropdown.Item>
              )}
              {currentScheduledFor && (
                <Dropdown.Item
                  leadingIcon={<SvgIcon name="calendar-x" />}
                  onClick={() => formCtx.handleSubmit(unschedulePost)()}
                >
                  <T id="Generics.Unschedule" defaultMessage="Unschedule" />
                </Dropdown.Item>
              )}
            </Dropdown.Items>
          </Dropdown>
        </div>
        {schedulePopupOpen && (
          <SchedulePostPopup
            open={schedulePopupOpen}
            value={currentScheduledFor}
            setOpen={setSchedulePopupOpen}
            onSubmit={setScheduledFor}
          />
        )}
      </div>
    </div>
  )
}
